// src/components/WatchlistSidePanel.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { isDarkMode, get_max_watchlist_items } from "../utils/constants";

import WatchlistUpdate from "../../javascript/images/WatchlistUpdate.png";
import { updateUserWatchlist, refreshWatchlist } from "../api/user";
import { getPricingData } from "../api/initiate_websocket_data";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useAppContext } from "../contexts/AppContext";
import { watchlistPaywallModalMessage } from "../utils/constants";
import { useChatContext } from "../contexts/ChatContext";
import { isMobile } from "react-device-detect";
import { createLatestRequestGuard } from "../utils/latestRequestGuard";
import { withheldWatchlistQuote } from "../utils/watchlistData";

export default function WatchlistSidePanel({
  /* required props – same as before */
  watchlistData,
  setWatchlistData,
  onQuestionSubmit,
  lastRequestedUpdateWatchlist,
  setLastRequestedUpdateWatchlist,
  isRefreshing,
  setIsRefreshing,
  hasErroredWatchlist,
  setHasErroredWatchlist,
  userData,
  hasSubscription,
  setActivePanel,
}) {
  const { appProps, websocketSubscriptionRef } = useAppContext();
  const { setIsSidebarOpen, activeThread } = useChatContext();
  const { setShowPaywallModal, setPaywallMessage, setPanel } = appProps

  const [tickersSearched, setTickersSearched] = useState([]);
  const [updatedTime, setUpdatedTime] = useState(null);
  const [watchlistCountError, setWatchlistCountError] = useState(false);
  const [numWatchlistItems, setNumWatchlistItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null); // <IconButton> anchor
  const [menuRow, setMenuRow] = useState(null);
  const [highlightTickerId, setHighlightTickerId] = useState(null);
  const highlightTimeoutRef = useRef(null);
  const listRef = useRef(null);
  const searchRequestGuardRef = useRef(createLatestRequestGuard());

  useEffect(() => {
    if (!activeThread?.threadUuid) return
    getPricingData(activeThread?.threadUuid);
  }, [userData, activeThread?.threadUuid]);

  const triggerSearch = (q) => {
    if (!q) return;
    const requestId = searchRequestGuardRef.current.begin();
    fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `{ searchTickers(searchTerm:"${q}") { id ticker name assetType exchange } }`,
      }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (!searchRequestGuardRef.current.isLatest(requestId)) return;
        setTickersSearched(d.data?.searchTickers || []);
      })
      .catch(() => {
        if (!searchRequestGuardRef.current.isLatest(requestId)) return;
        setTickersSearched([]);
      });
  };

  useEffect(() => () => searchRequestGuardRef.current.invalidate(), []);

  useEffect(() => {
    const itemCount = Object.values(watchlistData).flat().length;
    setNumWatchlistItems(itemCount);

    const times = Object.values(watchlistData)
      .flat()
      .map((i) => new Date(i.updatedAt || 0));
    if (times.length) {
      setUpdatedTime(
        new Date(Math.min(...times)).toLocaleString("en-US", {
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    }
  }, [watchlistData]);

  useEffect(() => {
    if (typeof window === "undefined") return () => {};

    const triggerHighlight = (tickerId) => {
      if (tickerId == null) return;
      const nextId = String(tickerId);
      setHighlightTickerId(nextId);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightTickerId((current) => (current === nextId ? null : current));
      }, 6000);
    };

    const handleHighlight = (event) => {
      triggerHighlight(event?.detail?.tickerId);
    };

    window.addEventListener("watchlist:highlight", handleHighlight);
    const pending = window.__warpWatchlistHighlight;
    if (pending?.tickerId != null) {
      triggerHighlight(pending.tickerId);
      window.__warpWatchlistHighlight = null;
    }

    return () => {
      window.removeEventListener("watchlist:highlight", handleHighlight);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!highlightTickerId || !listRef.current) return;
    const target = listRef.current.querySelector(
      `[data-watchlist-ticker-id="${highlightTickerId}"]`,
    );
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightTickerId, watchlistData]);

  // This activates the websocket for the watchlist to send data!
  // And pings to keep it open so long as the watchlist is open.
  useEffect(() => {
    const sub = websocketSubscriptionRef.current
    if (!sub) return;
    // Ping immediately, then every 10s (less than 30s TTL).
    const touch = () => {
      try {
        sub.perform("set_feature", {data: {feature: "watchlist", on: true, ttl: 30} });
      } catch (_) {}
    };
    touch();
    const id = setInterval(touch, 10_000);
    // Helpful: if the tab was backgrounded (timers throttled), refresh on focus.
    const onVis = () => { if (!document.hidden) touch(); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      try {
        sub.perform("set_feature", {data: {feature: "watchlist", on: false} });
      } catch (_) {}
    };
  }, []);

  const textString = isDarkMode
    ? "base-text dark watchlist-text"
    : "base-text watchlist-text";

  const textClass = isDarkMode
    ? "base-text dark watchlist-text"
    : "base-text watchlist-text";
  const watchlistPanelColors = isDarkMode
    ? {
        highlightBg: "rgba(99,102,241,0.12)",
        highlightBorder: "rgba(129,140,248,0.7)",
        highlightShadow: "0 0 0 1px rgba(129,140,248,0.55)",
      }
    : {
        highlightBg: "rgba(59,130,246,0.08)",
        highlightBorder: "rgba(59,130,246,0.6)",
        highlightShadow: "0 0 0 1px rgba(59,130,246,0.45)",
      };

  const formatNumber = (num) => {
    if (num == null || num == undefined) return ""
    const [whole, decimal] = num.toString().split(".");
    if (whole.length >= 5) {
      return whole; // Remove decimals
    }
    return num;
  };

  let classPlaceholderInput = isDarkMode
    ? "watchlist-placeholder-input dark"
    : "watchlist-placeholder-input";

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    if (event.target.value.length >= 2) {
      triggerSearch(event.target.value);
    } else {
      searchRequestGuardRef.current.invalidate();
      setTickersSearched([]);
    }
  };

  const openMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };
  const handleDelete = () => {
    if (!menuRow) return;
    updateUserWatchlist(null, menuRow.id, setWatchlistData, setIsRefreshing);
    closeMenu();
  };

  const addNewTicker = (id) => {
    let newNumWatchlistItems = numWatchlistItems + 1;
    if (newNumWatchlistItems > get_max_watchlist_items(userData)) {
      setWatchlistCountError(true);
    } else {
      setIsRefreshing(true);
      setNumWatchlistItems(newNumWatchlistItems);
      setSearchQuery("");
      searchRequestGuardRef.current.invalidate();
      setTickersSearched([]);
      setLastRequestedUpdateWatchlist(null); // Set this back to null to allow it to update again
      updateUserWatchlist([id], null, setWatchlistData, setIsRefreshing)
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        px: 2,
        pt: 1,
      }}
    >
      {watchlistCountError ? (
        <Typography
          sx={{ textAlign: "center", mb: 2 }}
          variant="body2"
          color="red"
        >
          You have reached the limit of items to add to your Watchlist
        </Typography>
      ) : null}

      
        <Box sx={{ 
          display: 'flex',
          width: '100%',
        }}>
          <Box
            sx={{
              position: "relative",
              marginBottom: 2,
              width: '100%',
            }}
          >
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => {
                // No paywall for now.. 
                if (true) { //hasSubscription) {
                  handleSearchChange(e)
                } else {
                  if (isMobile) {
                    setPanel("chat"); // so that it doesn't show back arrow when closed
                    setIsSidebarOpen(false)
                  }
                  setPaywallMessage(watchlistPaywallModalMessage)
                  setShowPaywallModal(true)
                }
              }}
              onBlur={() => setTimeout(() => {
                searchRequestGuardRef.current.invalidate();
                setTickersSearched([]);
              }, 150)}
              onFocus={handleSearchChange}
              className={classPlaceholderInput}
              style={{
                width: "90%", // full width of the centring box
                height: 24,
                padding: "8px 16px",
                borderRadius: 20,
                backgroundColor: isDarkMode ? "#3B3B3B" : "#EBEEF3",
              }}
            />

            {tickersSearched.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "calc(100% + 8px)", // 8 px gap below input
                  left: 0,
                  width: "100%",
                  backgroundColor: isDarkMode ? "#131521" : "#F6F6F6",
                  borderRadius: 1,
                  boxShadow: 3,
                  zIndex: 10,
                  maxHeight: 240,
                  overflowY: "auto",
                }}
              >
                {tickersSearched.map((row, i) => (
                  <Box
                    key={row.id}
                    sx={{
                      px: 2,
                      py: 1,
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      color: isDarkMode ? "#F6F6F6" : "#1D1D1F",
                      "&:hover": {
                        backgroundColor: isDarkMode ? "#2a2f3a" : "#e0e0e0",
                        cursor: "pointer",
                      },
                      ...(i === 0 && {
                        borderTopLeftRadius: 1,
                        borderTopRightRadius: 1,
                      }),
                      ...(i === tickersSearched.length - 1 && {
                        borderBottomLeftRadius: 1,
                        borderBottomRightRadius: 1,
                      }),
                    }}
                    onClick={() => addNewTicker(row.id)}
                  >
                    <Box>
                      <Typography variant="body2">
                        {row.name}&nbsp;({row.ticker})
                      </Typography>
                      <Typography variant="body2" style={{ fontSize: 10 }}>
                        {row.exchange}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      style={{
                        marginLeft: "auto",
                        fontStyle: "italic",
                        fontSize: 11,
                      }}
                    >
                      {row.assetType}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Box sx={{ height: "100%", overflowY: "auto", pr: 1 }} ref={listRef}>
          {Object.entries(watchlistData).map(([section, items]) =>
            !items.length ? null : (
              <Box key={section} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", mb: 1 }}>
                  <Box
                    className={textClass}
                    sx={{ width: "45%", fontWeight: "bold", fontSize: 16 }}
                  >
                    {section === "ForexPairs" ? "Forex Pairs" : section}
                  </Box>
                  <Box
                    className={textClass}
                    sx={{
                      width: "25%",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: "bold",
                    }}
                  >
                    PRICE
                  </Box>
                  <Box
                    className={textClass}
                    sx={{
                      width: "20%",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: "bold",
                    }}
                  >
                    CHANGE
                  </Box>
                  {/* <Box
                    className={textClass}
                    sx={{
                      width: "15%",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: "bold",
                    }}
                  >
                    ACTION
                  </Box> */}
                </Box>

                {items.map((row) => {
                  const show$ = ["stocks", "cryptos"].includes(row.assetType);
                  const withheldQuote = withheldWatchlistQuote(row);
                  const isHighlighted =
                    highlightTickerId != null &&
                    String(row.id) === highlightTickerId;
                  const priceColor = row.pricePctChange1d?.startsWith("-")
                    ? "#E34234"
                    : "green";
                  return (
                    <Box
                      key={row.id}
                      component="div"
                      data-watchlist-ticker-id={row.id ?? ""}
                      onClick={() => {
                        if (typeof onQuestionSubmit === "function") {
                          if (isMobile) {
                            setPanel("chat"); // so that it doesn't show back arrow when closed
                            setIsSidebarOpen(false) // close side bar on mobile
                          }
                          onQuestionSubmit(
                            // `Analyze ${row.ticker} ${row.action}`,
                            `Analyze ${row.ticker}`,
                            null,
                            [row.id]
                          );
                        }
                      }}
                      sx={{
                        position: "relative",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: 1.7,
                        cursor: "pointer",
                        backgroundColor: isHighlighted
                          ? watchlistPanelColors.highlightBg
                          : "transparent",
                        border: `1px solid ${
                          isHighlighted
                            ? watchlistPanelColors.highlightBorder
                            : "transparent"
                        }`,
                        boxShadow: isHighlighted
                          ? watchlistPanelColors.highlightShadow
                          : "none",
                        transition:
                          "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
                        "&:hover": {
                          backgroundColor: isHighlighted
                            ? watchlistPanelColors.highlightBg
                            : isDarkMode
                              ? "#2a2f3a"
                              : "#e0e0e0",
                        },
                        borderRadius: '8px',
                      }}
                    >
                      <Box
                        className={textClass}
                        sx={{
                          width: "45%",
                          display: "flex",
                          flexDirection: "column",
                          textAlign: "left",
                          lineHeight: "140%", // keeps rows compact
                          padding: '8px 0px 8px 12px',
                          marginRight: '8px',
                        }}
                      >
                        <Box sx={{ fontWeight: 600, fontSize: 12 }}>
                          {row.ticker}
                        </Box>
                        <Box sx={{ fontSize: 10, color: isDarkMode ? "#B7B7B7" : "#929292"}}>
                          {row.name}
                        </Box>
                      </Box>
                      <Box
                        className={textClass}
                        sx={{ width: "25%", textAlign: "left" }}
                      >
                        {withheldQuote ? (
                          <span title={withheldQuote.title}>{withheldQuote.label}</span>
                        ) : (
                          <>
                            {show$ && "$"}
                            {formatNumber(row.currentPrice)}
                          </>
                        )}
                      </Box>
                      <Box
                        className={textClass}
                        sx={{
                          width: "20%",
                          textAlign: "left",
                          color: `${priceColor} !important`,
                        }}
                      >
                        {row.pricePctChange1d}
                      </Box>
                      {/* <Box
                        className={textClass}
                        sx={{ width: "15%", textAlign: "left" }}
                      >
                        {row.action
                          ? row.action[0].toUpperCase() + row.action.slice(1)
                          : "—"}
                      </Box> */}
                      <Box sx={{ width: "10%", textAlign: "left", paddingRight: '2px', }}>
                        <IconButton
                          sx={{ color: isDarkMode ? "white" : "black" }}
                          onClick={(e) => openMenu(e, row)}
                        >
                          {" "}
                          <MoreHorizIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )
          )}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 1,
              pt: 1.5,
              pb: 3.5,
              background: isDarkMode
                ? "linear-gradient(to top, #222222 0%, #222222 72%, rgba(34,34,34,0.9) 88%, rgba(34,34,34,0) 100%)"
                : "linear-gradient(to top, #F9F9F9 0%, #F9F9F9 72%, rgba(249,249,249,0.9) 88%, rgba(249,249,249,0) 100%)",
            }}
          >
            <Box
              className="base-text"
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: "#2971D7",
                cursor: "pointer",
                mb: 1.25,
              }}
              onClick={() => {
                // No paywall for now..
                if (true) { //hasSubscription) {
                  const ids = Object.values(watchlistData)
                    .flat()
                    .map((i) => i.id);
                  const tickers = Object.values(watchlistData)
                    .flat()
                    .map((i) => `${i.ticker}`) //.map((i) => `${i.ticker} ${i.action}`)
                    .join(", ");
                  onQuestionSubmit(`Analyze ${tickers}`, null, ids);
                  setActivePanel("chat");
                } else {
                  setPaywallMessage(watchlistPaywallModalMessage)
                  setShowPaywallModal(true)
                }
              }}
            >
              Run Watchlist Analysis
            </Box>

            {/* <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Box
                className="base-text"
                sx={{ fontSize: 12, color: isDarkMode ? "#B7B7B7" : "#727279" }}
              >
                Updated {updatedTime || "---"}
              </Box>
              <Box
                sx={{ cursor: isRefreshing ? "not-allowed" : "pointer" }}
                onClick={() => {
                  if (isRefreshing) return;
                  const refreshProps = {
                    lastRequestedUpdateWatchlist,
                    setLastRequestedUpdateWatchlist,
                    setIsRefreshing,
                    setWatchlistData,
                    setHasErroredWatchlist,
                  };
                  refreshWatchlist(refreshProps, null, null, true);
                }}
              >
                <img
                  src={WatchlistUpdate}
                  style={{
                    width: 24,
                    // transform: isRefreshing ? "rotate(360deg)" : "none",
                    // transition: "transform 0.6s linear",
                  }}
                  className={isRefreshing ? "rotate-animation" : ""}
                />
              </Box>
            </Box> */}

            <Box
              className="base-text"
              sx={{
                fontSize: 10,
                color: isDarkMode ? "#B7B7B7" : "#727279",
              }}
            >
              {/* <b>Disclaimer:</b> This analysis is for informational purposes only and
              should not be considered financial advice. Always consider your
              financial situation and consult a financial adviser before making
              investment decisions. */}
              <b>Disclaimer:</b> Not financial advice, can make mistakes.
            </Box>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#222" : "#E9E9E9",
            "& .MuiMenuItem-root:hover": {
              backgroundColor: isDarkMode ? "#333" : "#D6D6D6",
            },
          },
        }}
      >
        <MenuItem sx={{ color: "#FF5252" }} onClick={handleDelete}>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}
